import { useState } from "react";
import { Link } from "react-router-dom";
import { Upload as UploadIcon, FileSpreadsheet, AlertCircle } from "lucide-react";
import { leadsApi, type LeadImportBatchDto, type LeadSource } from "@/api/leads";
import { Badge, Button, Input, Label } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";

/** v3.0 §13.4 — CSV upload page with column guidance + results summary. */
export function LeadImportPage() {
  const [file, setFile] = useState<File | null>(null);
  const [defaultSource, setDefaultSource] = useState<LeadSource>("PurchasedList");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<LeadImportBatchDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setImporting(true);
    setError(null);
    setResult(null);
    try {
      const dto = await leadsApi.import(file, defaultSource);
      setResult(dto);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Link to="/leads" className="inline-block mb-space-2 text-caption text-gold-shadow hover:underline">
        ← Leads
      </Link>
      <PageHeader
        eyebrow="Leads · Import"
        title="Import leads"
        description="Upload a CSV to bulk-create leads. Duplicates (by phone or email) are skipped."
      />

      {!result && (
        <div className="max-w-xl grid gap-space-4">
          <div className="border border-border-light rounded-md p-space-4 bg-white-pure">
            <div className="font-display font-semibold text-label uppercase tracking-label text-gold-shadow mb-space-2">
              CSV format
            </div>
            <p className="text-small text-gray-dark mb-space-2">
              Required columns: <code className="font-mono">FirstName</code>,{" "}
              <code className="font-mono">LastName</code>,{" "}
              <code className="font-mono">Phone</code>,{" "}
              <code className="font-mono">State</code>.
            </p>
            <p className="text-small text-gray-dark">
              Optional: <code className="font-mono">CompanyName</code>, Title, Email, Address, City, Zip, Source, SourceDetail, Notes.
              Column names are case-insensitive.
            </p>
          </div>

          <div className="grid gap-space-3">
            <div>
              <Label>CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <div>
              <Label>Default source (for rows without a Source column)</Label>
              <select
                className="h-10 w-full rounded-md border border-border-light bg-white-pure px-space-2 text-small"
                value={defaultSource}
                onChange={(e) => setDefaultSource(e.target.value as LeadSource)}
              >
                {(["PurchasedList","ColdCall","InboundInquiry","TradeShow","Referral","WebForm","IBBA","MaSource","LinkedIn","BizBuySell","Other"] as LeadSource[]).map(s =>
                  <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {error && (
              <div className="flex items-start gap-space-2 text-small text-status-error">
                <AlertCircle size={14} strokeWidth={1.5} aria-hidden /> {error}
              </div>
            )}

            <Button onClick={submit} disabled={!file || importing}>
              <UploadIcon size={14} strokeWidth={1.5} aria-hidden />
              {importing ? "Importing…" : "Import"}
            </Button>
          </div>
        </div>
      )}

      {result && <ImportResults result={result} onReset={() => { setResult(null); setFile(null); }} />}
    </>
  );
}

function ImportResults({ result, onReset }: { result: LeadImportBatchDto; onReset: () => void }) {
  const errors = parseErrors(result.errorLog);
  return (
    <div className="max-w-3xl grid gap-space-4">
      <div className="border border-border-light rounded-md p-space-4 bg-white-pure">
        <div className="flex items-center gap-space-2 font-display font-semibold text-label uppercase tracking-label text-gold-shadow mb-space-3">
          <FileSpreadsheet size={14} strokeWidth={1.5} aria-hidden /> Import results
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-space-3">
          <Stat label="Total rows" value={result.totalRows} />
          <Stat label="Imported" value={result.importedCount} accent="success" />
          <Stat label="Duplicates" value={result.duplicateCount} accent="warning" />
          <Stat label="Errors" value={result.errorCount} accent={result.errorCount > 0 ? "error" : "default"} />
        </div>

        <div className="mt-space-3 text-small">
          <Badge
            status={
              result.status === "Completed" ? "finalized" :
              result.status === "CompletedWithErrors" ? "review" :
              result.status === "Failed" ? "error" : "draft"
            }
          >
            {result.status}
          </Badge>
          <span className="ml-space-2 font-mono text-caption text-gray-mid">{result.fileName}</span>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="border border-border-light rounded-md overflow-hidden">
          <div className="px-space-3 py-space-2 bg-white-warm font-semibold text-caption uppercase tracking-wide">
            Row errors ({errors.length})
          </div>
          <table className="w-full text-small">
            <tbody>
              {errors.slice(0, 50).map((err, i) => (
                <tr key={i} className="border-t border-border-light">
                  <td className="px-space-3 py-space-2 font-mono text-caption w-16">{err.row}</td>
                  <td className="px-space-3 py-space-2 text-status-error">{err.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex gap-space-2">
        <Link to={`/leads?importBatchId=${result.id.replace(/-/g, "")}`}>
          <Button variant="secondary" size="sm">View imported leads</Button>
        </Link>
        <Button variant="text" size="sm" onClick={onReset}>Import another</Button>
      </div>
    </div>
  );
}

function Stat({
  label, value, accent = "default",
}: { label: string; value: number; accent?: "default" | "success" | "warning" | "error" }) {
  const color =
    accent === "success" ? "text-status-success" :
    accent === "warning" ? "text-status-warning" :
    accent === "error" ? "text-status-error" : "text-onyx";
  return (
    <div>
      <div className="text-caption uppercase tracking-wide text-gray-mid">{label}</div>
      <div className={"font-display text-h3 " + color}>{value}</div>
    </div>
  );
}

function parseErrors(log?: string | null): { row: number; message: string }[] {
  if (!log) return [];
  try { return JSON.parse(log) as { row: number; message: string }[]; } catch { return []; }
}
